import { PnlHierarchyMember } from 'src/components/star-grid/starGridTypes';

/**
 * Relabel a member tree from an alias map, leaving everything else alone.
 *
 * Applied between the fetch and `indexMemberTree`, NOT in the Name column's renderer: search, Copy and
 * the context menu all read `row.label`, so a renderer-only swap would display the alias while search
 * kept matching the old name.
 *
 * Structure is untouched — same nodes, same CHILD ORDER. The tree arrives sorted by its baked label and
 * stays that way: re-sorting under the alias would move rows out from under a selection mid-edit.
 *
 * Position keys are built from member IDs, so relabelling invalidates nothing: `checked`, `scope`,
 * `excluded` and `ops` all survive an alias switch (unlike a hierarchy switch, which changes the member
 * universe).
 *
 * @param labels `lower(element)` → alias, as the server returns it. Ids are lowercased to look up
 *   because a member id carries the closure's ancestor spelling while the export's element is raw.
 */
export const relabelTree = (members: readonly PnlHierarchyMember[], labels: Readonly<Record<string, string>>): PnlHierarchyMember[] =>
  members.map((member) => {
    const key = member.id.toLowerCase();
    // hasOwnProperty guard: a plain map lookup on an id like "constructor" would resolve to a
    // prototype value; an element with no alias must keep its registered label rather than go blank.
    const label = Object.prototype.hasOwnProperty.call(labels, key) ? labels[key] : member.label;
    return { ...member, label, children: relabelTree(member.children, labels) };
  });

/**
 * Relabel every member with its own ELEMENT ID — Prophecy's "system name".
 *
 * Not an alias and not fetched: the id is already on every node, so this is the one display mode that needs
 * no request and cannot fail. It is what you want when two members share a display name, or when an id has
 * to be copied out to write a query against the fact.
 *
 * Structure and child order are untouched, exactly as for an alias: relabelling must never move rows.
 */
export const relabelToIds = (members: readonly PnlHierarchyMember[]): PnlHierarchyMember[] =>
  members.map((member) => ({ ...member, label: member.id, children: relabelToIds(member.children) }));
